/**
 * Type definitions for the Accounting Rules module.
 *
 * Bank rules and Integration rules share the same Rule shape but
 * differ in which condition keys are valid and what "Applied to"
 * can point at. The `tab` field discriminates between them.
 *
 * This file is the ONLY source of truth for Rule-related types.
 * Mock data, utilities, hooks, and UI components all import from
 * here so a type change is a single-file edit.
 *
 * When the backend ships, the API response shape maps 1:1 to Rule
 * and useRules swaps mock data for fetch with zero component edits.
 */

export type RuleTab = "bank" | "integration";

export type RuleStatus = "active" | "paused";

/** Keys available in a condition. Bank-only vs integration-only noted. */
export type RuleConditionKey =
  // Shared
  | "payee"
  | "amount"
  // Bank-only
  | "direction"
  | "bankAccount"
  // Integration-only
  | "orderStatus"
  | "orderTotal"
  | "transactionType"
  | "lineItemType"
  | "paymentMethod"
  | "productCategory";

export type RuleConditionOperator =
  | "contains"
  | "equals"
  | "starts_with"
  | "ends_with"
  | "less_than"
  | "greater_than"
  | "between"
  | "is";

export interface RuleCondition {
  id: string;
  key: RuleConditionKey;
  operator: RuleConditionOperator;
  value: string | number;
  /** Only used for the "between" operator (lower + upper bound). */
  value2?: string | number;
}

export type RuleConditionJoin = "AND" | "OR";

/** What transactions a rule applies to (bank vs integration source). */
export type RuleAppliedTo =
  | {
      type: "all_bank_accounts";
    }
  | {
      type: "specific_bank_account";
      bankAccountId: string;
      bankAccountName: string;
      bankBrand: BankBrand;
    }
  | {
      type: "integration_source";
      integrationSource: IntegrationSource;
      integrationName: string;
    };

export type BankBrand =
  | "rbc"
  | "td"
  | "scotia"
  | "bmo"
  | "cibc"
  | "national_bank"
  | "tangerine"
  | "simplii"
  | "desjardins"
  | "other";

export type IntegrationSource =
  | "stripe"
  | "shopify"
  | "square"
  | "amazon"
  | "paypal"
  | "ebay"
  | "etsy";

export interface RuleAction {
  category?: string;
  account?: string;
  class?: string;
  location?: string;
  reimbursable?: boolean;
  billable?: boolean;
  gstTreatment?: "included" | "not_applicable" | "add_gst" | "zero_rated";
  memo?: string;
}

export interface Rule {
  id: string;
  tab: RuleTab;
  priority: number;
  name: string;
  appliedTo: RuleAppliedTo;
  conditions: RuleCondition[];
  conditionJoin: RuleConditionJoin;
  actions: RuleAction;
  status: RuleStatus;
  autoPost: boolean;
  matchCount: number;
  lastAppliedAt: string | null;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  velaSuggested?: boolean;
}

export interface RuleFilter {
  search: string;
  status: "all" | "active" | "paused";
}

/** Customize-drawer state (persisted to localStorage via useRulesCustomization). */
export type RuleSortField = "priority" | "name" | "lastApplied" | "matchCount";
export type RuleSortDirection = "asc" | "desc";
export type RulePageSize = 25 | 50 | 100 | 200;
export type RuleRowDensity = "comfortable" | "compact";

export type RuleColumnId =
  | "priority"
  | "name"
  | "appliedTo"
  | "conditions"
  | "settings"
  | "autoPost"
  | "status"
  | "actions";

export interface RuleColumn {
  id: RuleColumnId;
  label: string;
  visible: boolean;
}

export interface RulesCustomization {
  sortBy: RuleSortField;
  sortDirection: RuleSortDirection;
  pageSize: RulePageSize;
  rowDensity: RuleRowDensity;
  columns: RuleColumn[];
}

export const DEFAULT_COLUMNS: RuleColumn[] = [
  { id: "priority", label: "Priority", visible: true },
  { id: "name", label: "Rule name", visible: true },
  { id: "appliedTo", label: "Applied to", visible: true },
  { id: "conditions", label: "Conditions", visible: true },
  { id: "settings", label: "Settings", visible: true },
  { id: "autoPost", label: "Auto-post", visible: true },
  { id: "status", label: "Status", visible: true },
  { id: "actions", label: "Actions", visible: true },
];

export const DEFAULT_CUSTOMIZATION: RulesCustomization = {
  sortBy: "priority",
  sortDirection: "asc",
  pageSize: 50,
  rowDensity: "comfortable",
  columns: DEFAULT_COLUMNS,
};